import { useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { HugeiconsIcon } from "@hugeicons/react";
import { Mail01FreeIcons } from "@hugeicons/core-free-icons";

import { RegistrationFormLayout } from "../FormLayout.tsx";
import { RegistrationFormHeader } from "../FormHeader.tsx";

import { useRegistrationStore } from "@/store/registrationStore.ts";
import { Input } from "@/components/Input.tsx";
import { Checkbox } from "@/components/Checkbox.tsx";
import { Button } from "@/components/Button.tsx";
import type { Step1FormData } from "@/schemas/registrationSchemas.ts";
import { Step1Schema } from "@/schemas/registrationSchemas.ts";
import { i18n } from "@/localization";
import type { TranslationKey } from "@/localization/resources/keys.ts";
import { handleErrorMessage, isClientError } from "@/api/utils.ts";
import FormErrorMessage from "@/components/FormErrorMessage.tsx";
import { useRequestVerificationCodeMutation } from "@/api/emailVerification.ts";
import type { LegalDocumentKey } from "@/features/legal/LegalDocumentOverlay.tsx";
import { LegalDocumentOverlay } from "@/features/legal/LegalDocumentOverlay.tsx";
import { LegalDocumentTrigger } from "@/features/legal/LegalDocumentTrigger.tsx";
import { PERSON_NAME_MAX_LENGTH } from "@/utils/validation.ts";

const nameErrorParams = { max: PERSON_NAME_MAX_LENGTH };

export default function RegistrationStep1() {
	const { form, setField, nextStep } = useRegistrationStore();
	const { mutateAsync: requestCodeAsync } = useRequestVerificationCodeMutation();
	const [legalDoc, setLegalDoc] = useState<LegalDocumentKey | null>(null);
	const closeLegalDoc = useCallback(() => setLegalDoc(null), []);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		watch,
		setValue,
		setError
	} = useForm<Step1FormData>({
		resolver: zodResolver(Step1Schema),
		// First validation is submit-only anyway (RHF default); this keeps errors from
		// re-validating on every keystroke after a failed submit — they update on the next submit.
		reValidateMode: "onSubmit",
		defaultValues: {
			firstName: form.firstName ?? "",
			lastName: form.lastName ?? "",
			email: form.email,
			agbAccepted: false
		}
	});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const email = params.get("email");

		if (email) {
			setField("email", email);
			setValue("email", email, { shouldValidate: true });

			// Remove email param from URL
			params.delete("email");
			const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
			window.history.replaceState(null, "", newUrl);
		}
	}, [setField, setValue]);

	const onSubmit = async (data: Step1FormData) => {
		try {
			setField("firstName", data.firstName);
			setField("lastName", data.lastName);
			setField("email", data.email);

			if (data.email) {
				const res = await requestCodeAsync({
					email: data.email,
					firstName: data.firstName,
					lastName: data.lastName
				});
				setField("challenge", res.challenge);
				setField("codeExpirySeconds", res.codeExpirySeconds);

				nextStep();
			}
		} catch (e) {
			if (isClientError(e)) {
				setError("email", { message: handleErrorMessage(e) });

				return;
			}

			setError("root", { type: "manual", message: i18n("step1.requestCodeError") });
		}
	};

	return (
		<RegistrationFormLayout>
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col" aria-busy={isSubmitting}>
				<RegistrationFormHeader title={i18n("step1.title")} description={i18n("step1.description")} />

				<div className="grid gap-3 pt-10 sm:grid-cols-2">
					<Input
						label={`${i18n("form.firstName")} *`}
						type="text"
						placeholder={i18n("form.firstName")}
						{...register("firstName")}
						error={
							errors.firstName?.message &&
							i18n(errors.firstName.message as TranslationKey, nameErrorParams)
						}
					/>
					<Input
						label={`${i18n("form.lastName")} *`}
						type="text"
						placeholder={i18n("form.lastName")}
						{...register("lastName")}
						error={
							errors.lastName?.message && i18n(errors.lastName.message as TranslationKey, nameErrorParams)
						}
					/>
				</div>

				<div className="grid gap-10 pt-6">
					<Input
						label={`${i18n("form.email")} *`}
						type="email"
						placeholder={i18n("form.email")}
						prefixes={<HugeiconsIcon icon={Mail01FreeIcons} size={20} />}
						{...register("email")}
						error={errors.email?.message && i18n(errors.email.message as TranslationKey)}
					/>

					<Checkbox
						checked={watch("agbAccepted")}
						{...register("agbAccepted")}
						error={errors.agbAccepted?.message && i18n(errors.agbAccepted.message as TranslationKey)}
						label={
							<>
								{i18n("step1.agb.prefix")}{" "}
								<LegalDocumentTrigger
									doc="terms"
									label={i18n("step1.agb.terms")}
									onOpen={setLegalDoc}
								/>{" "}
								{i18n("step1.agb.middle")}{" "}
								<LegalDocumentTrigger
									doc="privacy"
									label={i18n("step1.agb.privacy")}
									onOpen={setLegalDoc}
								/>
								{i18n("step1.agb.suffix")}
							</>
						}
					/>

					{errors.root && <FormErrorMessage>{errors.root.message}</FormErrorMessage>}

					<Button type="submit" loading={isSubmitting}>
						{i18n("step1.submit")}
					</Button>
				</div>
			</form>

			{/* Outside the form, not inside the checkbox's label: the overlay portals into
			    document.body, but React still propagates its clicks through this JSX tree. */}
			<LegalDocumentOverlay doc={legalDoc} onClose={closeLegalDoc} />
		</RegistrationFormLayout>
	);
}
